import { Component, ChangeDetectionStrategy, input, computed, effect, signal } from '@angular/core';
import { AbstractControl, ValidationErrors } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ToolTipData } from '../../core/utils/tooltip-utils';

@Component({
    selector: 'app-error-display',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './error-display.component.html',
    styleUrl: './error-display.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ErrorDisplayComponent {
    // Use input() function instead of @Input() decorator for modern Angular
    control = input.required<AbstractControl | null>();
    customMessage = input<string>();
    customErrorMessageProvider = input<((errors: any) => string | void) | null>(null);
    showIcon = input<boolean>(true);
    untouchedIfEmpty = input<boolean>(false);

    // Character counting inputs
    showCharacterCount = input<boolean>(false);
    maxLength = input<number | null>(null);
    currentLength = input<number>(0);

    // Signal to track form control state manually (following Angular best practices)
    private controlState = signal<{
        errors: ValidationErrors | null;
        touched: boolean;
        dirty: boolean;
        value: any;
    } | null>(null);

    // Computed properties following Angular best practices
    hasErrors = computed(() => {
        const state = this.controlState();
        return !!(state?.errors && Object.keys(state.errors).length > 0);
    });

    // Character counting computed properties
    characterCount = computed(() => this.currentLength());
    isOverLimit = computed(() => {
        const max = this.maxLength();
        const current = this.currentLength();
        return max !== null && current > max;
    });
    shouldShowCharacterCount = computed(() =>
        this.showCharacterCount() && this.maxLength() !== null
    );
    compactCharacterText = computed(() =>
        `${this.currentLength()}/${this.maxLength()}`
    );

    errorMessage = computed(() => this.getErrorMessage());
    shouldShowError = computed(() => this.shouldDisplayError());

    constructor() {
        // Effect to sync form control state with our signal
        effect(() => {
            const formControl = this.control();
            if (formControl) {
                // Function to update state
                const updateState = () => {
                    this.controlState.set({
                        errors: formControl.errors,
                        touched: formControl.touched,
                        dirty: formControl.dirty,
                        value: formControl.value
                    });
                };

                // Set initial state
                updateState();

                // Subscribe to multiple events to catch all state changes
                const statusSubscription = formControl.statusChanges.subscribe(updateState);
                const valueSubscription = formControl.valueChanges.subscribe(updateState);

                // Override markAsTouched to trigger our update
                const originalMarkAsTouched = formControl.markAsTouched.bind(formControl);
                formControl.markAsTouched = (opts?: any) => {
                    originalMarkAsTouched(opts);
                    updateState();
                };

                // Cleanup all subscriptions
                return () => {
                    statusSubscription.unsubscribe();
                    valueSubscription.unsubscribe();
                    formControl.markAsTouched = originalMarkAsTouched;
                };
            } else {
                this.controlState.set(null);
            }
        });
    }

    /**
     * Determines if error should be displayed based on field state
     */
    private shouldDisplayError(): boolean {
        // Show error if character limit is exceeded
        if (this.isOverLimit()) {
            return true;
        }

        const state = this.controlState();
        if (!state || !this.hasErrors()) {
            return false;
        }

        // Handle untouchedIfEmpty behavior for optional fields
        if (this.untouchedIfEmpty()) {
            const isEmpty = !state.value ||
                (typeof state.value === 'string' && state.value.trim() === '');

            // Only hide error if field is empty AND untouched
            if (isEmpty && !state.touched) {
                return false;
            }

            // If field has value, show error regardless of touched state
            if (!isEmpty) {
                return true;
            }
        }

        // Show error if field has been touched or is dirty
        return state.touched || state.dirty;
    }

    /**
     * Get error message using existing ToolTipData utility
     */
    private getErrorMessage(): string {
        // Check for character limit exceeded first (priority over other errors)
        if (this.isOverLimit()) {
            return 'Character limit exceeded';
        }

        if (!this.hasErrors()) {
            return '';
        }

        // Use custom message if provided
        if (this.customMessage()) {
            return this.customMessage()!;
        }

        const state = this.controlState();
        if (state?.errors) {
            // Try custom error message provider first (for ActionService integration)
            const customProvider = this.customErrorMessageProvider();
            if (customProvider) {
                const customError = customProvider(state.errors);
                if (customError && typeof customError === 'string') {
                    return customError;
                }
            }

            // Fall back to existing ToolTipData utility for consistency
            return ToolTipData.getTooltipData(state.errors);
        }

        return 'This field is invalid.';
    }
}
